import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { firebaseAuth } from '../../../lib/firebase';
import { Button } from '../../../components/ui/button';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import { ApiError } from '../../../lib/api';
import { portalGreeting } from '../../../lib/portalGreeting';
import { liveSectionsPhrase } from '../../../lib/portalNav';
import type { PortalAccountSummary } from '../../../types/portalUser';
import { fetchPortalMe, fetchPortalTeam } from './portalApi';
import { usePortalViewerNav } from './usePortalViewerNav';
import { AccountManagerCard, TeamList } from './PortalTeam';
import { PortalShell } from './PortalShell';

// CLIENT PORTAL — the signed-in page.
//
// ⚠️⚠️ PHASE 0 SHOWS THE NAMES OF THE ACCOUNTS YOU HAVE ACCESS TO, AND NOTHING
// ELSE. That is not an unfinished page — it is the point. The access boundary is
// what this phase ships, and it deserves to be reviewed on its own rather than
// alongside a report everyone is excited about. There is deliberately nothing on
// this screen that would matter if the scope were wrong, which is exactly the
// condition under which the boundary can be picked apart safely.
//
// Phase 1 puts the report here, behind the `reporting` permission.

function accountTypeLabel(a: PortalAccountSummary): string | null {
  if (!a.isGrant) return null;
  switch (a.accountType) {
    case 'group':
    case 'head_office':
    case 'region':
      return 'Group';
    case 'brand':
      return 'Brand';
    default:
      return null;
  }
}

export function PortalHome() {
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  // ⚠️ ITS OWN KEY AND ITS OWN REQUEST (§4/§5). `retry: false` and no error surface:
  // the page is complete without it.
  const team = useQuery({ queryKey: ['portal-team'], queryFn: fetchPortalTeam, retry: false });
  // ⚠️ Round 6: the sentence below names THIS viewer's live sections, from the
  // same per-viewer nav the menu renders.
  const viewerNav = usePortalViewerNav();

  if (me.isLoading) {
    return (
      <PortalShell>
        <CenteredSpinner />
      </PortalShell>
    );
  }

  // ⚠️ A 403 here is the ordinary "this Firebase identity has no portal access"
  // case — most often a kyck staff member who followed a portal link, but also
  // anyone who signed themselves in with an uninvited address (Firebase will
  // mint an identity for any inbox; only an invite grants anything). It is NOT
  // an error state, so it does not read as one, and it does NOT sign them out:
  // deciding to end someone else's session on their behalf is rude when the
  // likely fix is closing the tab.
  if (me.error instanceof ApiError && me.error.status === 403) {
    return (
      <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null}>
        <div className="mx-auto max-w-md text-center">
          <h1 className="text-xl font-semibold text-foreground">No portal access</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {firebaseAuth.currentUser?.email
              ? `${firebaseAuth.currentUser.email} doesn't have access to the client portal.`
              : "This account doesn't have access to the client portal."}{' '}
            If you were expecting access, contact your Kyck account manager.
          </p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => void signOut(firebaseAuth)}
          >
            Sign in with a different account
          </Button>
        </div>
      </PortalShell>
    );
  }

  if (me.error || !me.data) {
    return (
      <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null}>
        <div className="mx-auto max-w-md text-center">
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We couldn't load your portal. Try again in a moment.
          </p>
          <Button variant="outline" className="mt-6" onClick={() => void me.refetch()}>
            Try again
          </Button>
        </div>
      </PortalShell>
    );
  }

  const { displayName, email, accounts, permissions } = me.data;

  return (
    // ⚠️ `nav` is opt-in per page. The nav resolves its own contents from the
    // SAME cached `/me` this page reads, so it costs no second request — and it
    // is absent on every page with no session, rather than offering a
    // signed-out visitor sections they cannot open.
    <PortalShell signedInAs={email} nav>
      {/* ⚠️ A TITLE IS NOT A NAME. `displayName` is `[title, firstName,
          lastName]` joined, so taking the first token greeted a contact who has
          a title and no first name with "Hi Mr". The composition lives in
          src/lib/portalGreeting.ts — fixed in the RENDER, because a contact with
          a title and no first name is legitimate data. */}
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        {portalGreeting(displayName, email)}
      </h1>

      {accounts.length === 0 ? (
        // ⚠️ An honest empty state, never a silent blank. This is what a revoked
        // or misconfigured grant looks like, and it should send the client to a
        // human rather than leave them refreshing.
        <p className="mt-4 max-w-prose text-sm text-muted-foreground">
          You don't have access to any accounts yet. Contact your Kyck account manager and
          they'll set it up.
        </p>
      ) : (
        <>
          {/* ⚠️⚠️ THE WELCOME COPY (§2). Scott's draft said "so excited", "amazing
              clients" and "level up". That collides with kyck's own voice guide: `elevate`
              and `unlock` are banned words and "level up" is their casual twin, and the
              guide's own do/don't row answers this exactly — swap the FEELING for the
              CONCRETE COMMITMENT ("A strategist owns your account and picks up the phone").
              Same four promises, one idea per sentence, Australian English.

              ⚠️ IT MAKES NO CADENCE CLAIM ("updated daily" and the like). The report
              carries its own provenance lines; a freshness promise in static copy is a
              figure with no source.

              ⚠️ AND IT DOES NOT REPEAT THE NAME — the greeting above already has it. */}
          <p className="mt-3 max-w-prose text-sm text-muted-foreground">
            Welcome to your Kyck portal.
          </p>
          <p className="mt-2 max-w-prose text-sm text-muted-foreground">
            Today it holds your advertising report: leads, cost per lead and spend across
            Google and Meta.
          </p>
          {/* ⚠️⚠️ SCOTT'S WORDING, TAKEN AS WRITTEN (§6). "+ so much more" is the
              register `voice.md:40` rejects and I flagged it last round; he has now
              chosen it twice. HIS CALL, RECORDED, NOT RE-RAISED.

              ⚠️ "Community management" and "campaign strategies" are promised here and
              BOTH are in the nav (Organic social → Community management; Campaigns →
              Strategy), so the copy and the navigation agree. A promise the nav does
              not echo reads as something we forgot to build. */}
          <p className="mt-2 max-w-prose text-sm text-muted-foreground">
            Over the coming months it will hold more. Your approved club assets, your live
            campaigns, organic social, community management, campaign strategies, video and
            image examples and tutorials + so much more.
          </p>

          {/* ⚠️⚠️ THE PARENTHETICAL IS DERIVED, NEVER TYPED (§4). "just reports right
              now" is true until the second live nav item ships, at which point it is
              WRONG on a client-facing page — and a hand-typed count is a promise to
              remember something nobody will. `liveSectionsPhrase` reads the SAME
              `liveNavItems()` the nav renders from, so the two cannot disagree.

              ⚠️ AND IT NAMES THE BURGER, because below 900px the menu is behind it and
              there is no visible menu to point at. "Use the menu" alone sends a phone
              reader looking for something that is not on their screen. */}
          <p className="mt-4 max-w-prose text-sm text-muted-foreground">
            Use the menu to move around the portal ({liveSectionsPhrase(viewerNav?.items ?? [])}). On a small
            screen it's behind the Menu button at the top.
          </p>

          {/* ⚠️⚠️ THE "Your advertising report" CARD IS GONE (§1), AND THAT WAS VERIFIED
              IN A BROWSER BEFORE IT WENT. The nav is the route in — but the brief flagged
              ~232px of empty dark rail in Scott's screenshot, and if the nav were genuinely
              absent here, removing the card would strand the client with no way to reach
              their report at all. Measured on this page: the nav paints 232 × 777, five
              links, the first a real 204 × 36 box.

              ⚠️ THE NO-PERMISSION BRANCH STAYS. Without `reporting` the nav hides its
              Reports section entirely, so this is the only thing that would tell such a
              client anything at all — and "not on your plan" is a different statement from
              "coming soon". */}
          {!permissions.includes('reporting') && (
            <p className="mt-3 max-w-prose text-sm text-muted-foreground">
              Reporting is not switched on for your account yet. Your account manager can
              turn it on.
            </p>
          )}

          {/* ⚠️ THE ACCOUNTS LIST IS ORIENTATION, NOT A CONTROL (§3). Six full-width
              rows at ~53px each is a table where a list would do, and the nav is how a
              client actually opens a report. Name, the group marker where it applies,
              nothing else. */}
          <h2 className="mt-8 text-xs uppercase tracking-wide text-muted-foreground">
            {accounts.length === 1 ? 'Your account' : `Your ${accounts.length} accounts`}
          </h2>
          <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
            {accounts.map((a) => (
              <li key={a.id} className="text-sm text-foreground">
                {a.name}
                {accountTypeLabel(a) && (
                  <span className="ml-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                    {accountTypeLabel(a)}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {/* ⚠️⚠️ THE TEAM (§4/§5). ITS OWN QUERY, not a widening of `/me`: that key
              is shared with the nav and the shell's brand theme, so a field added there is
              paid for on every portal page.

              ⚠️ A FAILED OR PENDING FETCH RENDERS NOTHING — no skeleton, no error. The
              page is complete without it, and an error box about a contact list would tell
              a client their portal is broken when their report is fine. */}
          {team.data?.accountManager && (
            <div className="mt-8">
              <AccountManagerCard member={team.data.accountManager} />
            </div>
          )}
          {team.data && <TeamList members={team.data.team} industries={team.data.industries} />}
        </>
      )}
    </PortalShell>
  );
}
